import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useSessionContext } from "../contexts/SessionContext";
import { apiFetch } from "../lib/api";

const GITHUB_NEW_ISSUE_URL =
  "https://github.com/Shootingers/roll1/issues/new";

export interface ElementContext {
  testId: string;
  tag: string;
  text: string;
  rect: { x: number; y: number; width: number; height: number };
  ancestors: string[];
  componentName?: string;
  componentChain?: string[];
  sourceFile?: string;
}

function collectEnvContext(sessionCode: string, role: string): string {
  return [
    `**Browser**: ${navigator.userAgent}`,
    `**Screen**: ${window.screen.width}×${window.screen.height} (DPR ${window.devicePixelRatio})`,
    `**Viewport**: ${window.innerWidth}×${window.innerHeight}`,
    `**Session**: ${sessionCode || "—"}`,
    `**Role**: ${role}`,
    `**URL**: ${window.location.pathname}`,
    `**Time**: ${new Date().toISOString()}`,
  ].join("\n");
}

function formatElementContext(el: ElementContext): string {
  const lines = [
    `**Element**: \`<${el.tag}>\` ${el.testId ? `(data-testid="${el.testId}")` : ""}`,
  ];
  if (el.componentName) {
    lines.push(`**Component**: ${el.componentName}${el.sourceFile ? ` (${el.sourceFile})` : ""}`);
  }
  if (el.componentChain && el.componentChain.length > 1) {
    lines.push(`**Component chain**: ${el.componentChain.join(" → ")}`);
  }
  lines.push(`**Text**: ${el.text || "—"}`);
  lines.push(
    `**Position**: ${Math.round(el.rect.x)},${Math.round(el.rect.y)} (${Math.round(el.rect.width)}×${Math.round(el.rect.height)})`,
  );
  if (el.ancestors.length > 0) {
    lines.push(`**Ancestors**: ${el.ancestors.join(" → ")}`);
  }
  return lines.join("\n");
}

function openGitHubFallback(title: string, body: string) {
  const params = new URLSearchParams({ title, body, labels: "bug" });
  window.open(`${GITHUB_NEW_ISSUE_URL}?${params.toString()}`, "_blank", "noopener");
}

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  elementContext: ElementContext | null;
}

export default function BugReportDialog({
  open,
  onOpenChange,
  elementContext,
}: BugReportDialogProps) {
  const { t } = useI18n();
  const { mp, isGM } = useSessionContext();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) { clearInterval(id); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [retryAfter > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && elementContext) {
      const label = elementContext.componentName || elementContext.testId || elementContext.tag;
      setTitle(`[${label}] `);
    }
  }, [open, elementContext]);

  const handleClose = useCallback(() => {
    setTitle("");
    setDescription("");
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || submitting) return;

    const sessionCode = mp.session?.code ?? "";
    const role = isGM ? t('bugReport.roleGM') : t('bugReport.rolePlayer');

    const elCtx = elementContext ? formatElementContext(elementContext) : undefined;
    const envCtx = collectEnvContext(sessionCode, role);

    setSubmitting(true);
    let wasRateLimited = false;

    try {
      const res = await apiFetch("/api/bug-reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          elementContext: elCtx,
          envContext: envCtx,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        toast(t("bugReport.submitted"), {
          action: data.url
            ? { label: "GitHub", onClick: () => window.open(data.url, "_blank", "noopener") }
            : undefined,
        });
      } else if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        const seconds = data.retryAfter ?? 60;
        setRetryAfter(seconds);
        toast.error(t("bugReport.rateLimited"));
        wasRateLimited = true;
      } else {
        // Server unavailable or no token — fallback to direct GitHub
        const sections = [
          t('bugReport.sectionDescription'), "", description.trim() || t('bugReport.noDescription'),
        ];
        if (elCtx) sections.push("", t('bugReport.sectionElement'), "", elCtx);
        sections.push("", t('bugReport.sectionContext'), "", envCtx);
        openGitHubFallback(title.trim(), sections.join("\n"));
        toast(t("bugReport.submitted"));
      }
    } catch {
      // Network error — fallback
      const sections = [
        t('bugReport.sectionDescription'), "", description.trim() || t('bugReport.noDescription'),
      ];
      if (elCtx) sections.push("", t('bugReport.sectionElement'), "", elCtx);
      sections.push("", t('bugReport.sectionContext'), "", envCtx);
      openGitHubFallback(title.trim(), sections.join("\n"));
      toast(t("bugReport.submitted"));
    } finally {
      setSubmitting(false);
      if (!wasRateLimited) {
        handleClose();
      }
    }
  }, [title, description, mp.session, isGM, elementContext, t, handleClose, submitting]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="bug-report-dialog">
        <DialogHeader>
          <DialogTitle>{t("bugReport.title")}</DialogTitle>
          <DialogDescription>{t("bugReport.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {elementContext && (
            <div className="rounded-md bg-zinc-800 p-2 text-xs text-zinc-400 font-mono">
              {elementContext.componentName && (
                <span className="text-blue-400 font-semibold">{elementContext.componentName} </span>
              )}
              &lt;{elementContext.tag}&gt;
              {elementContext.testId && (
                <span className="text-zinc-300"> [{elementContext.testId}]</span>
              )}
              {elementContext.text && (
                <span className="text-zinc-500 ml-1">"{elementContext.text}"</span>
              )}
              {elementContext.sourceFile && (
                <div className="text-zinc-600 mt-0.5">{elementContext.sourceFile}</div>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="bug-title">{t("bugReport.titleLabel")}</Label>
            <Input
              id="bug-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("bugReport.titlePlaceholder")}
              maxLength={200}
              autoFocus
              data-testid="bug-report-title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bug-description">{t("bugReport.descriptionLabel")}</Label>
            <Textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("bugReport.descriptionPlaceholder")}
              rows={3}
              maxLength={2000}
              data-testid="bug-report-description"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleClose}>
            {t("bugReport.cancel")}
          </Button>
          {retryAfter > 0 && (
            <span className="text-sm text-muted-foreground">
              {t("bugReport.retryIn").replace("{{seconds}}", String(retryAfter))}
            </span>
          )}
          <Button onClick={handleSubmit} disabled={!title.trim() || submitting || retryAfter > 0}>
            {submitting ? "..." : t("bugReport.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
